import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquarePlus, Send, Trash2, Loader2, Bot, User as UserIcon, Sparkles, Paperclip, BookOpen, Mail, ListChecks, X } from 'lucide-react';
import { oustazApi } from '../../api/oustaz';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { EmptyState } from './ui';
import { MarkdownLite } from './MarkdownLite';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const MODELS = [
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 · fast' },
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8 · most capable' },
];
const DEFAULT_MODEL = 'claude-sonnet-4-6';

const STARTERS = [
  { icon: BookOpen, label: 'Lesson plan', text: 'Create a 45-minute lesson plan for [topic] for grade [X], including learning objectives, a warm-up, main activities, and a short assessment.' },
  { icon: Mail, label: 'Parent email', text: 'Write a warm, professional email to a parent about their child\'s progress and behaviour this term.' },
  { icon: ListChecks, label: 'Quiz idea', text: 'Generate a 5-question multiple-choice quiz on [topic], with the correct answers and a short explanation for each.' },
];

const stripHtml = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim().slice(0, 10000);
  } catch { return (html || '').slice(0, 10000); }
};

export default function OustazChat({ orgId }) {
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [sending, setSending] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [attached, setAttached] = useState(null); // { title, text }
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadChats = useCallback(async () => {
    if (!orgId) return;
    try { setChats(await oustazApi.listChats(orgId)); } catch { /* ignore */ }
  }, [orgId]);

  useEffect(() => { loadChats(); }, [loadChats]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openChat = async (id) => {
    setActiveId(id); setLoadingChat(true);
    try {
      const chat = await oustazApi.getChat(id, orgId);
      setMessages(chat.messages || []);
      setModel(chat.model || DEFAULT_MODEL);
    } catch { toast.error('Failed to open conversation'); }
    finally { setLoadingChat(false); }
  };

  const newChat = () => { setActiveId(null); setMessages([]); setInput(''); setAttached(null); };

  const handleStarter = (text) => { setInput(text); if (inputRef.current) inputRef.current.focus(); };

  const openAttach = async () => {
    setAttachOpen(true);
    setLoadingLessons(true);
    try { setLessons(await oustazApi.listGenerations(orgId)); }
    catch { toast.error('Failed to load lessons'); }
    finally { setLoadingLessons(false); }
  };

  const pickLesson = async (g) => {
    try {
      const full = await oustazApi.getGeneration(g.gen_id || g.id);
      const text = stripHtml(full.html_content || full.content || '');
      if (!text) { toast.error('This lesson has no readable content'); return; }
      setAttached({ title: full.title || g.title || 'Lesson', text });
      setAttachOpen(false);
      if (!input.trim()) setInput('Please refine and improve this lesson.');
      if (inputRef.current) inputRef.current.focus();
    } catch { toast.error('Failed to attach lesson'); }
  };

  const removeChat = async (id, e) => {
    e.stopPropagation();
    try {
      await oustazApi.deleteChat(id, orgId);
      if (id === activeId) newChat();
      setChats((c) => c.filter((x) => x.id !== id));
      toast.success('Conversation deleted');
    } catch { toast.error('Delete failed'); }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !attached) || sending) return;
    // Build the message: typed text + optional attached lesson context
    const content = attached
      ? `${text || 'Please refine and improve this lesson.'}\n\n---\n📎 Lesson to refine — "${attached.title}":\n${attached.text}`
      : text;
    setInput('');
    setAttached(null);
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content }]);
    try {
      if (!activeId) {
        const chat = await oustazApi.createChat({ organization_id: orgId, message: content, model });
        setActiveId(chat.id);
        setMessages(chat.messages || []);
        await loadChats();
      } else {
        const res = await oustazApi.sendChatMessage(activeId, { organization_id: orgId, content, model });
        setMessages((m) => [...m, res.assistant]);
        setChats((c) => c.map((x) => (x.id === activeId ? { ...x, title: res.title, updated_at: new Date().toISOString() } : x)));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Chat request failed';
      toast.error(detail);
      setMessages((m) => m.filter((mm, i) => !(i === m.length - 1 && mm.role === 'user')));
      setInput(text);
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-260px)] min-h-[480px]" data-testid="oustaz-chat">
      {/* Conversation list */}
      <div className="w-64 shrink-0 flex flex-col border border-border rounded-lg bg-card overflow-hidden">
        <div className="p-3 border-b border-border">
          <Button onClick={newChat} className="w-full gap-2" data-testid="chat-new-btn">
            <MessageSquarePlus className="w-4 h-4" /> New chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {chats.length === 0 && <p className="text-xs text-muted-foreground p-3 text-center">No conversations yet</p>}
          {chats.map((c) => (
            <button key={c.id} onClick={() => openChat(c.id)}
              className={cn('w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 group transition-colors',
                activeId === c.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted')}
              data-testid={`chat-item-${c.id}`}>
              <span className="truncate flex-1">{c.title || 'Untitled'}</span>
              <Trash2 className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-red-500 shrink-0"
                onClick={(e) => removeChat(c.id, e)} data-testid={`chat-delete-${c.id}`} />
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className="flex-1 flex flex-col border border-border rounded-lg bg-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <span className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI Assistant</span>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-56 h-8 text-xs" data-testid="chat-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
          {loadingChat && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
          {!loadingChat && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center gap-5 py-6">
              <EmptyState icon={Bot} title="Ask me anything" actionLabel={null}
                description="Lesson ideas, report-card remarks, admin help, or general questions — powered by Claude." />
              <div className="flex flex-wrap justify-center gap-2 max-w-lg" data-testid="chat-starters">
                {STARTERS.map((s) => (
                  <button key={s.label} onClick={() => handleStarter(s.text)}
                    className="flex items-center gap-2 px-3 py-2 rounded-full border border-border text-sm hover:bg-muted hover:border-primary/40 transition-colors"
                    data-testid={`chat-starter-${s.label.replace(/\s+/g, '-').toLowerCase()}`}>
                    <s.icon className="w-4 h-4 text-primary" /> {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn('flex gap-3', m.role === 'user' ? 'justify-end' : 'justify-start')} data-testid={`chat-msg-${m.role}`}>
              {m.role === 'assistant' && <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-primary" /></div>}
              <div className={cn('max-w-[75%] rounded-2xl px-4 py-2.5 text-sm break-words leading-relaxed',
                m.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap' : 'bg-muted text-foreground rounded-bl-sm')}>
                {m.role === 'assistant' ? <MarkdownLite content={m.content} /> : m.content}
              </div>
              {m.role === 'user' && <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4" /></div>}
            </div>
          ))}
          {sending && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-primary" /></div>
              <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3">
          {attached && (
            <div className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/20 text-xs w-fit max-w-full" data-testid="chat-attachment-chip">
              <Paperclip className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="truncate">Refining: <strong>{attached.title}</strong></span>
              <button onClick={() => setAttached(null)} className="text-muted-foreground hover:text-red-500 shrink-0" data-testid="chat-attachment-remove"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={openAttach} disabled={sending} title="Attach a lesson to refine" data-testid="chat-attach-btn">
              <Paperclip className="w-4 h-4" />
            </Button>
            <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Type a message…  (Enter to send, Shift+Enter for a new line)"
              className="min-h-[44px] max-h-40 resize-none" rows={1} data-testid="chat-input" disabled={sending} />
            <Button onClick={send} disabled={sending || (!input.trim() && !attached)} size="icon" className="h-11 w-11 shrink-0" data-testid="chat-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Attach lesson dialog */}
      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="chat-attach-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> Attach a lesson to refine</DialogTitle></DialogHeader>
          {loadingLessons ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : lessons.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No generated lessons yet. Create one in the Create tab first.</p>
          ) : (
            <div className="space-y-1.5">
              {lessons.map((g) => (
                <button key={g.gen_id || g.id} onClick={() => pickLesson(g)}
                  className="w-full text-left px-3 py-2.5 rounded-lg border border-border hover:border-primary/40 hover:bg-muted transition-colors flex items-center gap-3"
                  data-testid={`chat-lesson-${g.gen_id || g.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{g.title || 'Untitled'}</p>
                    <Badge variant="outline" className="mt-1 text-[10px]">{g.content_type_label || g.content_type}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
